import { useEffect, useRef, useMemo, useState } from 'react';
import {
  useSearchParams,
  useLocation,
  NavLink,
  Outlet,
} from 'react-router-dom';
import {
  TableMenu,
  IconLoader,
  Table,
  IconExpand,
  PermissionEnum,
  PermissionDenied,
  ToggleInput,
  ButtonSecondary,
  ButtonSize,
  IconDownload,
  DateInput,
  DateInputSize,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  setAccountsStateInitiate,
  clearAccountsClosingBalances,
} from '../store/slice';
import { Account } from '../types';
import { columns, columnsLabels } from './variables/list';
import formatAccountsList from './helpers/format-accounts-list';
import useListFunctions from './hooks/use-list';
import { setNotificationVisible } from '../../../../../../store/slice';

import ROUTES from '../../../routes';

import cn from './styles.module.css';

const AccountsList = (): React.JSX.Element => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    menuItems,
    accounts,
    menuItemsLoaded,
    pagination,
    accountsStateInititate,
    errorsList,
  } = useAppSelector((state) => state.accounts);
  const { overdraftEnabled } = useAppSelector(
    (state) => state.accountsDictionaries,
  );

  const [formattedColumns, setFormattedColumns] = useState<
    { name: string; label: string; hidden: boolean; sortable?: boolean }[]
  >(
    columns as {
      name: string;
      label: string;
      hidden: boolean;
      sortable?: boolean;
    }[],
  );
  const [showBalances, setShowBalances] = useState(false);
  const [closingBalancesAt, setClosingBalancesAt] = useState('');
  const { activePermissions, currenciesList: currencies } = useAppSelector(
    (state) => state.root,
  );

  const [searchParams] = useSearchParams();

  const { search } = useLocation();

  const dispatch = useAppDispatch();

  const categoryId = searchParams.get('categoryId');
  const groupId = searchParams.get('groupId');
  const primaryId = searchParams.get('primaryId');

  const {
    exportIsSuccess,
    exportIsLoading,
    accountsCagoriesIsLoading,
    accountsCategoriesIsFetching,
    isLoading,
    onSort,
    setPage,
    errorList,
    onExportClick,
    filtersComponent,
    onTableFilterChange,
    filters,
    sort,
    isFetching,
    onGetAccountsBalances,
    accountsBalancesIsLoading,
    accountsBalancesIsFetching,
    handleSubmit,
    onMenuCategoryClick,
    onMenuGroupClick,
    onMenuPrimaryAccountClick,
  } = useListFunctions({
    scrollRef,
    pagination,
    overdraftEnabled,
    currencies,
    accounts,
    setShowBalances,
    search,
    accountsStateInititate,
    setFormattedColumns,
    showBalances,
    categoryId,
    groupId,
    primaryId,
    menuItems,
    activePermissions,
    closingBalancesAt,
    setClosingBalancesAt,
  });

  const formattedData = useMemo(() => {
    const nextAccounts = accounts?.map((item) => {
      const result = { ...item };

      currencies?.forEach((currency) => {
        result[currency.value] = '-';
      });

      return result;
    });

    const data = formatAccountsList(
      nextAccounts as Account[],
      currencies,
      overdraftEnabled,
      closingBalancesAt,
    );

    return data;
  }, [accounts, currencies, overdraftEnabled, closingBalancesAt]);

  useEffect(() => {
    if (!activePermissions?.includes(PermissionEnum.ACCOUNTS_CHART)) {
      dispatch(setAccountsStateInitiate(true));
    }
  }, [activePermissions]);

  useEffect(() => {
    if (exportIsSuccess) {
      dispatch(
        setNotificationVisible({
          visible: true,
          text: "Report in progress. You'll be notified.",
          type: 'accounts',
        }),
      );
    }
  }, [exportIsSuccess]);

  let content;
  let accountsTitle;

  if (categoryId && groupId && primaryId) {
    const currentCategory = menuItems?.find(
      (item) => item.category.id === categoryId,
    );

    if (currentCategory) {
      const currentGroup = currentCategory.category.groups?.find(
        (item) => item.id === groupId,
      );

      if (currentGroup) {
        accountsTitle = currentGroup.accounts?.find(
          (item) => item.id === primaryId,
        )?.label as string;
      }
    }
  } else if (categoryId && groupId) {
    const currentCategory = menuItems?.find(
      (item) => item.category.id === categoryId,
    );

    if (currentCategory) {
      accountsTitle = currentCategory.category.groups?.find(
        (item) => item.id === groupId,
      )?.label as string;
    }
  } else if (categoryId) {
    accountsTitle = menuItems?.find((item) => item.category.id === categoryId)
      ?.category.label as string;
  }

  if (
    !menuItemsLoaded ||
    accountsCagoriesIsLoading ||
    accountsCategoriesIsFetching ||
    isLoading
  ) {
    content = (
      <div className={cn.accountsLoader}>
        <IconLoader />
      </div>
    );
  } else if (activePermissions?.includes(PermissionEnum.ACCOUNTS_LIST)) {
    let isFirefox = false;
    const { userAgent } = navigator;

    if (userAgent.indexOf('Firefox') !== -1) {
      isFirefox = true;
    }

    content = (
      <>
        <div
          className={cn.accountListLeft}
          style={isFirefox ? { scrollbarWidth: 'thin' } : undefined}
        >
          <TableMenu
            headerText="Chart of account"
            onMenuToggle={() => {}}
            menuItems={menuItems}
            baseUrl="/accounts/list"
            onCategoryClick={(id: string) => onMenuCategoryClick(id)}
            onGroupClick={({ categoryId: menuCategoryId, id }) =>
              onMenuGroupClick({ menuCategoryId, id })
            }
            onPrimaryAccountClick={({
              categoryId: menuCategoryId,
              groupId: menuGroupId,
              id,
            }) =>
              onMenuPrimaryAccountClick({ menuCategoryId, menuGroupId, id })
            }
          />
        </div>
        <div className={cn.accountListRight}>
          <div className={cn.accountsTopWrapper}>
            <div className={cn.accountsTitle}>
              {`Accounts list ${accountsTitle ? `of ${accountsTitle}` : ''}`}
            </div>
            {activePermissions?.includes(
              PermissionEnum.ACCOUNTS_CREATE_ACTION,
            ) && (
              <div className={cn.createAccountButtonWrapper}>
                <NavLink
                  className={`${cn.createAccountButton} ${
                    !filters.primaryId ? cn.createAccountButtonDisabled : ''
                  }`}
                  to={`${ROUTES.ACCOUNTS_CREATE}${search || ''}`}
                >
                  <IconExpand />
                  <span>Add account</span>
                </NavLink>
                <div className={cn.createAccountHint}>
                  i
                  <div className={cn.accountHintContent}>
                    You can add an account only in Primary Account
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className={cn.accountListRigthTableWrapper}>
            <Table
              take="20"
              topContent={
                <div>
                  <div className={cn.accountControls}>
                    <ButtonSecondary
                      size={ButtonSize.SMALL}
                      text={exportIsLoading ? 'Loading...' : 'Export'}
                      icon={<IconDownload />}
                      className={`button-white ${cn.exportButton}`}
                      disabled={exportIsLoading}
                      onClick={onExportClick}
                    />
                    <div className={cn.showBalancesWrapper}>
                      <ToggleInput
                        name="showBalances"
                        checked={showBalances}
                        onChange={(e) => {
                          onGetAccountsBalances({ checked: e.target.checked });
                        }}
                        visualClassName={cn.showBalancesVisual}
                        title="Show Balances"
                      />
                    </div>
                    {showBalances && (
                      <div className={cn.balancesDateWrapper}>
                        <DateInput
                          size={DateInputSize.SMALL}
                          placeholder="Select balance date"
                          value={closingBalancesAt}
                          onChange={(e) => {
                            onGetAccountsBalances({
                              date: new Date(e).toISOString(),
                              checked: showBalances,
                            });
                          }}
                          disabledAfter={new Date()}
                          onReset={() => {
                            setClosingBalancesAt('');
                            dispatch(clearAccountsClosingBalances());
                          }}
                          withoutCustomSelector
                        />
                      </div>
                    )}
                  </div>
                  {pagination && pagination?.totalCount >= 0 && (
                    <div
                      className={cn.foundAccounts}
                    >{`Found ${pagination.totalCount} accounts`}</div>
                  )}
                </div>
              }
              setPage={(data) => {
                const nextFilters = { ...filters };

                if (showBalances) {
                  delete nextFilters.includes;
                }

                if (closingBalancesAt) {
                  delete nextFilters.closingBalancesAt;
                }

                setPage({ ...data, filters: nextFilters });
              }}
              pagination={pagination}
              columns={formattedColumns}
              data={formattedData || []}
              onSort={(name) => {
                onSort(name);
              }}
              filters={filters}
              sort={sort}
              columnsLabels={columnsLabels}
              onFilterChange={(values) => {
                const nextFilters = { ...values };

                if (showBalances) {
                  delete nextFilters.includes;
                }

                if (closingBalancesAt) {
                  delete nextFilters.closingBalancesAt;
                }

                onTableFilterChange(nextFilters);
              }}
              filtersComponent={filtersComponent}
              handleSubmit={handleSubmit}
              isLoading={
                isFetching ||
                accountsBalancesIsLoading ||
                accountsBalancesIsFetching
              }
              storeKey="accounts-list"
              error={errorsList || errorList}
              withVerticalScroll
              withScroll
              withSettings
              clickable
            />
          </div>
        </div>
      </>
    );
  } else {
    content = <PermissionDenied />;
  }

  return (
    <div className={cn.accountsList} ref={scrollRef}>
      {content}
      <Outlet />
    </div>
  );
};

export default AccountsList;
